import clsx from 'clsx';
import React from 'react';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';

const cryptoSymbols: Record<string, string> = {
  ADA: '₳',
  BTC: '₿',
  DAI: '$',
  ETH: 'Ξ',
  SOL: '◎',
  USDC: '$',
  USDT: '$',
  USDG: '$',
  EURC: '€',
  CADC: 'CA$',
  CADT: 'CA$',
  XLM: 'XLM',
  UNI: 'UNI',
  BCH: '₿',
  LTC: 'Ł',
  AAVE: 'AAVE',
  LINK: 'LINK',
  MATIC: 'MATIC',
  PTS: 'PTS',
};

export const getCurrencySymbol = (currency: string): string => {
  if (currency in cryptoSymbols) return cryptoSymbols[currency];
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
};

type FormatBillingAmountProps = {
  amount: number;
  currencyCode: CurrencyCode;
  currencyPrecision?: number;
  className?: string;
  secondary?: boolean;
};

/**
 * Renders an amount in the same format as the billing dashboard list (symbol + locale number).
 * Amount is in smallest units (e.g. cents for USD).
 */
export const FormatBillingAmount = ({
  amount,
  currencyCode,
  currencyPrecision,
  className,
  secondary = false,
}: FormatBillingAmountProps): JSX.Element => {
  const precision = currencyPrecision ?? currencyPrecisionMap[currencyCode];
  const displayAmount = amount / 10 ** precision;
  const displayPrecision = Math.min(precision, 6);
  const symbol = getCurrencySymbol(currencyCode);
  const formatted = displayAmount.toLocaleString('en-US', {
    minimumFractionDigits: displayPrecision,
    maximumFractionDigits: displayPrecision,
  });
  const isZero = amount === 0;
  const useMuted = secondary || isZero;
  return (
    <span
      className={clsx(useMuted ? 'text-main-500' : 'text-main', className)}
      data-testid="currency-text-field"
    >
      <span className="inline-flex items-baseline gap-x-[1ch]">
        <span>{symbol}</span>
        <span>{formatted}</span>
      </span>
    </span>
  );
};
